import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { trackingApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Truck, 
  MapPin, 
  Phone, 
  Star,
  Navigation,
  Clock,
  RefreshCw,
  Loader2
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom truck icon
const truckIcon = new L.DivIcon({
  className: 'custom-truck-marker',
  html: `<div style="background-color: #FF4500; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M10 17h4V5H2v12h3"></path>
      <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"></path>
      <circle cx="7.5" cy="17.5" r="2.5"></circle>
      <circle cx="17.5" cy="17.5" r="2.5"></circle>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

// Custom pickup marker
const pickupIcon = new L.DivIcon({
  className: 'custom-pickup-marker',
  html: `<div style="background-color: #22c55e; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.2);">
    <span style="color: white; font-weight: bold; font-size: 14px;">P</span>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

// Custom delivery marker
const deliveryIcon = new L.DivIcon({
  className: 'custom-delivery-marker',
  html: `<div style="background-color: #3b82f6; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.2);">
    <span style="color: white; font-weight: bold; font-size: 14px;">D</span>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

// Component to recenter map when location changes
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

export default function LiveTrackingMap({ jobId, isProvider = false }) {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);

  // Default center (Australia)
  const defaultCenter = [-25.2744, 133.7751];

  useEffect(() => {
    fetchTrackingInfo();
    
    // Poll for updates every 5 seconds if viewing as customer
    if (!isProvider) {
      intervalRef.current = setInterval(fetchTrackingInfo, 5000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      stopSharingLocation();
    };
  }, [jobId]);

  const fetchTrackingInfo = async () => {
    try {
      const response = await trackingApi.getInfo(jobId);
      setTracking(response.data);
      setError(null);
      if (response.data.current_location) {
        setLastUpdate(new Date(response.data.current_location.updated_at));
      }
    } catch (err) {
      setError('Failed to load tracking info');
    } finally {
      setLoading(false);
    }
  };

  const startSharingLocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    try {
      // Start tracking on backend
      await trackingApi.start(jobId);
      
      // Watch position
      watchIdRef.current = navigator.geolocation.watchPosition(
        async (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            heading: position.coords.heading,
            speed: position.coords.speed ? position.coords.speed * 3.6 : null // Convert m/s to km/h
          };
          
          try {
            await trackingApi.updateLocation(jobId, location);
            setLastUpdate(new Date());
            await fetchTrackingInfo();
          } catch (err) {
            console.error('Failed to update location:', err);
          }
        },
        (err) => {
          console.error('Geolocation error:', err);
          setError('Unable to access your location');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
      
      setIsSharing(true);
    } catch (err) {
      setError('Failed to start tracking');
    }
  };

  const stopSharingLocation = async () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    try {
      await trackingApi.stop(jobId);
    } catch (err) {
      console.error('Failed to stop tracking:', err);
    }
    
    setIsSharing(false);
  };

  const getMapCenter = () => {
    if (tracking?.current_location) {
      return [tracking.current_location.latitude, tracking.current_location.longitude];
    }
    return defaultCenter;
  };

  const formatTime = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 10) return 'Just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-sm p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
              <Navigation className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                LIVE TRACKING
              </h3>
              <div className="flex items-center gap-2 text-sm">
                {tracking?.tracking_active ? (
                  <Badge className="bg-green-500/15 text-green-500">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse" />
                    Live
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Not Active
                  </Badge>
                )}
                {lastUpdate && (
                  <span className="text-muted-foreground text-xs">
                    Updated {formatTime(lastUpdate)}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {isProvider ? (
            <Button
              onClick={isSharing ? stopSharingLocation : startSharingLocation}
              variant={isSharing ? "destructive" : "default"}
              size="sm"
            >
              {isSharing ? (
                <>Stop Sharing</>
              ) : (
                <>
                  <Navigation className="w-4 h-4 mr-2" />
                  Share Location
                </>
              )}
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={fetchTrackingInfo}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="h-[400px] relative">
        {tracking?.tracking_active && tracking?.current_location ? (
          <MapContainer
            center={getMapCenter()}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Truck marker */}
            <Marker
              position={[tracking.current_location.latitude, tracking.current_location.longitude]}
              icon={truckIcon}
            >
              <Popup>
                <div className="text-center">
                  <strong>{tracking.provider?.name || 'Driver'}</strong>
                  {tracking.current_location.speed && (
                    <p className="text-sm">{Math.round(tracking.current_location.speed)} km/h</p>
                  )}
                </div>
              </Popup>
            </Marker>
            
            <MapUpdater center={getMapCenter()} />
          </MapContainer>
        ) : (
          <div className="h-full flex items-center justify-center bg-muted/50">
            <div className="text-center">
              <Truck className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h4 className="font-semibold mb-2">Tracking Not Active</h4>
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                {isProvider 
                  ? "Click 'Share Location' to let the customer track your delivery in real-time"
                  : "The driver will start sharing their location once the pickup is complete"
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Driver Info */}
      {tracking?.provider && !isProvider && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                <Truck className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">{tracking.provider.name}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                  <span>{tracking.provider.rating?.toFixed(1) || 'New'}</span>
                </div>
              </div>
            </div>
            
            {tracking.provider.phone && (
              <a href={`tel:${tracking.provider.phone}`}>
                <Button variant="outline" size="sm">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Driver
                </Button>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Route Info */}
      <div className="p-4 border-t border-border grid grid-cols-2 gap-4 text-sm">
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 bg-green-500/15 rounded-full flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-green-500 text-xs font-bold">P</span>
          </div>
          <div>
            <p className="font-medium">Pickup</p>
            <p className="text-muted-foreground">
              {tracking?.pickup_location?.city}, {tracking?.pickup_location?.state}
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 bg-blue-500/15 rounded-full flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-blue-500 text-xs font-bold">D</span>
          </div>
          <div>
            <p className="font-medium">Delivery</p>
            <p className="text-muted-foreground">
              {tracking?.delivery_location?.city}, {tracking?.delivery_location?.state}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
